import { useCallback, useEffect, useMemo, useRef } from "react";
import { SupabaseInstance } from "@/integrations/supabase";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthContext from "./useAuthContext";
import { useToast } from "./useToast";
import { AUTH_EVENT } from "@/constants/authEvents";
import { ROUTES_PATH } from "@/router";
import { Logger } from "@/utils";
import {
  INFO_TITLE,
  UPDATE_PASSWORD_PROCCESS_MESSAGE,
} from "@/constants/messages";
import {
  TWO_SECONDS_TIMEOUT,
  FIVE_SECONDS_TIMEOUT,
} from "@/constants/timeouts";

export default function useAuthState() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const timeout = useRef<NodeJS.Timeout | null>(null);
  const {
    initialAuthChecked,
    user: currentUser,
    handleSetUser,
    handleSetSession,
    handleSetInitialSession,
    handleSetRecoveryPassword,
  } = useAuthContext();
  const currentUserRef = useRef(currentUser);

  const isUpdatePasswordRoute = useMemo(
    () => location.pathname.includes(ROUTES_PATH.UPDATE_PASSWORD),
    [location.pathname],
  );

  const handleContinueRecoveryPassword = useCallback(() => {
    Logger.addBreadcrumb({
      category: "auth_state_listener",
      message: "continue_recovery_password",
      level: "info",
    });
    handleSetRecoveryPassword(true);
    navigate(ROUTES_PATH.UPDATE_PASSWORD);
    toast({
      title: INFO_TITLE,
      description: UPDATE_PASSWORD_PROCCESS_MESSAGE,
    });
  }, [navigate, handleSetRecoveryPassword, toast]);

  useEffect(() => {
    const supabaseListener = SupabaseInstance.onAuthStateChange(
      (event, session) => {
        if (event === AUTH_EVENT.SIGNED_OUT) {
          Logger.addBreadcrumb({
            category: "auth_state_listener",
            message: "signed_out_event",
            level: "info",
          });
          handleSetUser(null);
          handleSetSession(null);
          navigate(ROUTES_PATH.LOGIN);
          return;
        }

        handleSetSession(session);
        // Only update user on specific events, not TOKEN_REFRESHED which has stale metadata
        if (
          event === AUTH_EVENT.SIGNED_IN || event === AUTH_EVENT.USER_UPDATED
        ) {
          const incomingUser = session?.user ?? null;
          // Supabase fires SIGNED_IN on every tab focus via _recoverAndRefresh().
          // Skip updating user if data hasn't changed to prevent unnecessary re-renders
          // (avoids flagLoading:true -> spinner that feels like a page reload).
          if (
            event === AUTH_EVENT.SIGNED_IN &&
            incomingUser?.id === currentUserRef.current?.id &&
            incomingUser?.updated_at === currentUserRef.current?.updated_at
          ) {
            // Session was already updated above with fresh tokens — user object unchanged
          } else {
            handleSetUser(incomingUser);
          }
        }
        if (
          event === AUTH_EVENT.PASSWORD_RECOVERY &&
          session?.access_token
        ) {
          handleSetRecoveryPassword(true);
          return;
        }
        if (
          event === AUTH_EVENT.SIGNED_IN &&
          session?.user?.user_metadata?.is_recovery_session === true
        ) {
          handleContinueRecoveryPassword();
          return;
        }

        // Only navigate on initial sign in or sign out, not on token refresh
        if (!initialAuthChecked && event === AUTH_EVENT.SIGNED_IN) {
          const authRoutes = [
            ROUTES_PATH.LOGIN,
            ROUTES_PATH.REGISTER,
            ROUTES_PATH.FORGOT_PASSWORD,
          ];
          if (authRoutes.includes(location.pathname)) {
            navigate(ROUTES_PATH.DASHBOARD);
          }
          return;
        }
        if (
          event === AUTH_EVENT.USER_UPDATED &&
          isUpdatePasswordRoute
        ) {
          Logger.addBreadcrumb({
            category: "auth_state_listener",
            message: "user_updated_event",
            level: "info",
          });
          SupabaseInstance.logout();
          if (timeout.current) clearTimeout(timeout.current);
          timeout.current = setTimeout(() => {
            handleSetUser(null);
            navigate(ROUTES_PATH.LOGIN);
          }, TWO_SECONDS_TIMEOUT);
        }
      },
    );
    return () => {
      supabaseListener.unsubscribe();
      clearTimeout(timeout.current ?? 0);
    };
  }, [
    initialAuthChecked,
    isUpdatePasswordRoute,
    navigate,
    handleSetUser,
    handleSetSession,
    handleSetRecoveryPassword,
    handleContinueRecoveryPassword,
  ]);

  useEffect(() => {
    let cancelled = false;

    // First get session (for tokens), then get fresh user data from server
    const initAuth = async () => {
      try {
        const { data: { session } } = await SupabaseInstance.getSession();

        if (cancelled) return;

        if (session) {
          // Get fresh user data from server (not cached)
          const { data: { user }, error } = await SupabaseInstance.getUser();
          if (!cancelled && !error && user) {
            handleSetInitialSession(session, user);
          } else {
            handleSetInitialSession(session, session.user ?? null);
          }
        } else {
          handleSetInitialSession(null, null);
        }
      } catch (_) {
        if (!cancelled) {
          handleSetInitialSession(null, null);
        }
      }
    };

    initAuth();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Keep ref in sync so the visibility handler always reads the latest user
  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  // Refresh user data when user returns to the tab (e.g., after visiting Stripe portal in new tab)
  useEffect(() => {
    let retryTimeout: NodeJS.Timeout | null = null;

    const fetchAndUpdateUser = async () => {
      try {
        const { data: { user }, error } = await SupabaseInstance.getUser();
        if (error || !user) return false;
        // Only update if user data actually changed to avoid unnecessary re-renders.
        // updated_at is more reliable than JSON.stringify(user_metadata) since
        // server may return metadata keys in different order.
        const prev = currentUserRef.current;
        if (prev?.id === user.id && prev?.updated_at === user.updated_at) {
          return false;
        }
        handleSetUser(user);
        return true;
      } catch (error) {
        Logger.loggerError({
          section: "visibility_change_refresh",
          message: error,
        });
        return false;
      }
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        if (window.location.pathname.startsWith(ROUTES_PATH.CHAT)) return;
        Logger.addBreadcrumb({
          category: "visibility_change_refresh",
          message: "get_user",
          level: "info",
        });

        const updated = await fetchAndUpdateUser();

        // If nothing changed on the first fetch, schedule a retry to catch
        // webhooks that may not have processed yet (e.g. Stripe portal cancellation).
        if (!updated) {
          retryTimeout = setTimeout(async () => {
            if (document.visibilityState === "visible") {
              await fetchAndUpdateUser();
            }
          }, FIVE_SECONDS_TIMEOUT);
        }
      } else {
        // Tab hidden — cancel pending retry to avoid stale updates
        if (retryTimeout) {
          clearTimeout(retryTimeout);
          retryTimeout = null;
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [handleSetUser]);
}
